import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { AdminShell } from '../components/layout/AdminShell';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { FieldHelp, FieldHelpGroup, SectionHelp } from '../components/FieldHelp';
import { Modal } from '../components/Modal';
import { StatusPill } from '../components/StatusPill';
import { useToast } from '../components/ToastHost';
import { deleteStaff, inviteStaff, listStaff, updateStaff } from '../lib/adminApi';
import { staffHelp } from '../lib/fieldHelpCopy';
import { emptyPermissions, type StaffMe, type StaffPermissions } from '../types/auth';
import { isAdmin, useStaffMe } from '../lib/permissions';

const ACTION_LABELS: Record<string, string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  telemetry_health: 'Telemetry health',
  reset: 'Provisioning reset',
  first_edit_available: 'First profile edit',
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function IconSuspend(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M8 12h8" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
    </svg>
  );
}

function IconActivate(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path
        d="M10 8.5v7l6-3.5-6-3.5z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconDelete(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 7h14M10 7V5h4v2M9 7v12a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PermissionMatrix({
  value,
  onChange,
}: {
  value: StaffPermissions;
  onChange: (next: StaffPermissions) => void;
}): JSX.Element {
  function toggle(area: keyof StaffPermissions, action: string, checked: boolean) {
    const next = structuredClone(value);
    (next[area] as Record<string, boolean>)[action] = checked;
    onChange(next);
  }

  const rows: Array<{
    area: keyof StaffPermissions;
    label: string;
    actions: string[];
    help: string;
  }> = [
    {
      area: 'clients',
      label: 'Clients',
      actions: ['view', 'create', 'edit'],
      help: staffHelp.clientsPerms,
    },
    {
      area: 'device_catalog',
      label: 'Device catalog',
      actions: ['create', 'edit'],
      help: staffHelp.deviceCatalogPerms,
    },
    {
      area: 'manufacturing_batches',
      label: 'Manufacturing batches',
      actions: ['create', 'edit'],
      help: staffHelp.manufacturingBatchesPerms,
    },
    {
      area: 'manufactured_devices',
      label: 'Manufactured devices',
      actions: ['create', 'edit', 'telemetry_health', 'reset'],
      help: staffHelp.manufacturedDevicesPerms,
    },
    {
      area: 'account',
      label: 'Account',
      actions: ['edit', 'first_edit_available'],
      help: staffHelp.accountPerms,
    },
  ];

  return (
    <div className="permission-matrix">
      {rows.map((row) => (
        <fieldset key={row.area} className="permission-matrix__group">
          <SectionHelp title={row.label} help={row.help} as="legend">
            {row.actions.map((action) => (
              <label key={action} className="catalog-form__check">
                <input
                  type="checkbox"
                  checked={Boolean((value[row.area] as Record<string, boolean>)[action])}
                  onChange={(e) => toggle(row.area, action, e.target.checked)}
                />
                <span>{actionLabel(action)}</span>
              </label>
            ))}
          </SectionHelp>
        </fieldset>
      ))}
    </div>
  );
}

export function StaffPage(): JSX.Element {
  const meQuery = useStaffMe();
  const me = meQuery.data;
  const queryClient = useQueryClient();
  const { success } = useToast();
  const listQuery = useQuery({
    queryKey: ['staff'],
    queryFn: listStaff,
    enabled: isAdmin(me),
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editRow, setEditRow] = useState<StaffMe | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    | null
    | { type: 'suspend' | 'activate' | 'delete'; row: StaffMe }
  >(null);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'admin' | 'operator'>('operator');
  const [permissions, setPermissions] = useState<StaffPermissions>(emptyPermissions());
  const [editRole, setEditRole] = useState<'admin' | 'operator'>('operator');
  const [editStatus, setEditStatus] = useState<'ACTIVE' | 'DISABLED'>('ACTIVE');
  const [editPermissions, setEditPermissions] = useState<StaffPermissions>(emptyPermissions());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editRow) return;
    setEditRole(editRow.role);
    setEditStatus((editRow.status as 'ACTIVE' | 'DISABLED') || 'ACTIVE');
    setEditPermissions(editRow.permissions ?? emptyPermissions());
  }, [editRow]);

  const inviteMutation = useMutation({
    mutationFn: () =>
      inviteStaff({
        email: email.trim(),
        display_name: displayName.trim() || undefined,
        role,
        permissions,
      }),
    onSuccess: async (invited) => {
      setInviteOpen(false);
      setEmail('');
      setDisplayName('');
      setRole('operator');
      setPermissions(emptyPermissions());
      setError(null);
      success(`Invited ${invited.email}`);
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editRow) throw new Error('No staff selected');
      return updateStaff(editRow.staff_user_id, {
        role: editRole,
        status: editStatus,
        permissions: editPermissions,
      });
    },
    onSuccess: async (updated) => {
      setEditRow(null);
      setError(null);
      success(`${updated.email} updated`);
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: ({
      staffUserId,
      status,
    }: {
      staffUserId: string;
      status: 'ACTIVE' | 'DISABLED';
      email: string;
    }) => updateStaff(staffUserId, { status }),
    onSuccess: async (_data, variables) => {
      setError(null);
      success(
        variables.status === 'ACTIVE'
          ? `${variables.email} activated`
          : `${variables.email} suspended`,
      );
      setConfirmAction(null);
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ staffUserId }: { staffUserId: string; email: string }) =>
      deleteStaff(staffUserId),
    onSuccess: async (_data, variables) => {
      setError(null);
      success(`${variables.email} deleted`);
      setConfirmAction(null);
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (meQuery.isLoading) {
    return (
      <AdminShell title="Staff" subtitle="Operators & access lamps">
        <div className="panel">
          <p className="loading-inline" aria-busy="true">
            Loading…
          </p>
        </div>
      </AdminShell>
    );
  }

  if (!isAdmin(me)) {
    return <Navigate to="/account" replace />;
  }

  const myId = me?.staff_user_id ?? '';
  const busy = statusMutation.isPending || deleteMutation.isPending;

  return (
    <AdminShell title="Staff" subtitle="Operators & access lamps">
      <div className="panel">
        <div className="panel-toolbar">
          <p className="panel-toolbar__meta">Staff users with HQ access</p>
          <div className="panel-toolbar__actions">
            <button
              className="primary-button"
              type="button"
              onClick={() => {
                setError(null);
                setInviteOpen(true);
              }}
            >
              Invite staff
            </button>
          </div>
        </div>
        {listQuery.isError ? (
          <div className="form-error">
            {listQuery.error instanceof Error ? listQuery.error.message : 'Failed to load staff'}
          </div>
        ) : null}
        {error && !inviteOpen && !editRow ? <div className="form-error">{error}</div> : null}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(listQuery.data ?? []).length === 0 && !listQuery.isLoading && !listQuery.isError ? (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state">
                      <p className="empty-state__title">No staff yet</p>
                      <p className="empty-state__copy">
                        Invite the first operator or admin to give them HQ access.
                      </p>
                      <button
                        className="primary-button"
                        type="button"
                        onClick={() => {
                          setError(null);
                          setInviteOpen(true);
                        }}
                      >
                        Invite staff
                      </button>
                    </div>
                  </td>
                </tr>
              ) : null}
              {(listQuery.data ?? []).map((row) => {
                const isSelf = row.staff_user_id === myId;
                const suspended = String(row.status || '').toUpperCase() === 'DISABLED';
                return (
                  <tr key={row.staff_user_id}>
                    <td>{row.email}</td>
                    <td>{row.display_name || '—'}</td>
                    <td>{row.role}</td>
                    <td>
                      <StatusPill status={row.status} />
                    </td>
                    <td className="table-actions">
                      <button
                        type="button"
                        className="ghost-button"
                        onClick={() => {
                          setError(null);
                          setEditRow(row);
                        }}
                      >
                        Edit
                      </button>
                      {!isSelf ? (
                        suspended ? (
                          <button
                            type="button"
                            className="table-icon-button table-icon-button--shine"
                            aria-label={`Activate ${row.email}`}
                            title="Activate"
                            disabled={busy}
                            onClick={() => {
                              setError(null);
                              setConfirmAction({ type: 'activate', row });
                            }}
                          >
                            <IconActivate />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="table-icon-button"
                            aria-label={`Suspend ${row.email}`}
                            title="Suspend"
                            disabled={busy}
                            onClick={() => {
                              setError(null);
                              setConfirmAction({ type: 'suspend', row });
                            }}
                          >
                            <IconSuspend />
                          </button>
                        )
                      ) : null}
                      {!isSelf ? (
                        <button
                          type="button"
                          className="table-icon-button table-icon-button--danger"
                          aria-label={`Delete ${row.email}`}
                          title="Delete"
                          disabled={busy}
                          onClick={() => {
                            setError(null);
                            setConfirmAction({ type: 'delete', row });
                          }}
                        >
                          <IconDelete />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {inviteOpen ? (
        <Modal
          title="Invite staff"
          onClose={() => setInviteOpen(false)}
          closeOnBackdrop={false}
          wide
        >
          <form
            className="login-form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              inviteMutation.mutate();
            }}
          >
            <FieldHelpGroup>
              <FieldHelp label="Email" help={staffHelp.email}>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FieldHelp>
              <FieldHelp label="Display name" help={staffHelp.displayName}>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </FieldHelp>
              <FieldHelp label="Role" help={staffHelp.role}>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'admin' | 'operator')}
                >
                  <option value="operator">operator</option>
                  <option value="admin">admin</option>
                </select>
              </FieldHelp>
              <PermissionMatrix value={permissions} onChange={setPermissions} />
            </FieldHelpGroup>
            {error ? <div className="form-error">{error}</div> : null}
            <div className="modal-card__actions">
              <button type="button" className="ghost-button" onClick={() => setInviteOpen(false)}>
                Cancel
              </button>
              <button className="primary-button" type="submit" disabled={inviteMutation.isPending}>
                {inviteMutation.isPending ? 'Inviting…' : 'Invite'}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {editRow ? (
        <Modal
          title={`Edit ${editRow.email}`}
          onClose={() => setEditRow(null)}
          closeOnBackdrop={false}
          wide
        >
          <form
            className="login-form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              updateMutation.mutate();
            }}
          >
            <FieldHelpGroup>
              <FieldHelp label="Role" help={staffHelp.role}>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as 'admin' | 'operator')}
                >
                  <option value="operator">operator</option>
                  <option value="admin">admin</option>
                </select>
              </FieldHelp>
              <FieldHelp label="Status" help={staffHelp.status}>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as 'ACTIVE' | 'DISABLED')}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="DISABLED">DISABLED</option>
                </select>
              </FieldHelp>
              <PermissionMatrix value={editPermissions} onChange={setEditPermissions} />
            </FieldHelpGroup>
            {error ? <div className="form-error">{error}</div> : null}
            <div className="modal-card__actions">
              <button type="button" className="ghost-button" onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button className="primary-button" type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      <ConfirmDialog
        open={confirmAction !== null}
        title={
          confirmAction?.type === 'delete'
            ? 'Delete staff'
            : confirmAction?.type === 'activate'
              ? 'Activate staff'
              : 'Suspend staff'
        }
        message={
          confirmAction?.type === 'delete'
            ? `Permanently delete ${confirmAction.row.email}? This removes their Cognito account, profile, and photo.`
            : confirmAction?.type === 'activate'
              ? `Activate ${confirmAction.row.email}? They will regain the ability to sign in to HQ.`
              : `Suspend ${confirmAction?.row.email}? They will not be able to sign in to HQ.`
        }
        confirmLabel={
          confirmAction?.type === 'delete'
            ? 'Delete'
            : confirmAction?.type === 'activate'
              ? 'Activate'
              : 'Suspend'
        }
        danger={confirmAction?.type === 'delete'}
        busy={busy}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          if (!confirmAction) return;
          setError(null);
          const { type, row } = confirmAction;
          if (type === 'delete') {
            deleteMutation.mutate({ staffUserId: row.staff_user_id, email: row.email });
          } else {
            statusMutation.mutate({
              staffUserId: row.staff_user_id,
              status: type === 'activate' ? 'ACTIVE' : 'DISABLED',
              email: row.email,
            });
          }
        }}
      />
    </AdminShell>
  );
}
